'use client';

import { useEffect, useRef } from 'react';

import {
  trackChatSuiteApplyComplete,
  trackChatSuiteApplyStart,
  trackChatSuiteDemoClick,
  trackChatSuiteFeatureInterest,
  trackChatSuitePageView,
  trackChatSuitePricingView,
} from '~/lib/analytics/chatsuite-events';

/**
 * Fires a chatsuite_page_view event once on mount.
 * Renders nothing visible.
 */
export function PageViewTracker({
  city,
  vertical,
  country,
}: {
  city: string;
  vertical: string;
  country: string;
}) {
  useEffect(() => {
    trackChatSuitePageView({ city, vertical, country });
  }, [city, vertical, country]);

  return null;
}

/**
 * Fires a chatsuite_pricing_view event when the pricing section scrolls
 * into the viewport (IntersectionObserver, fires once).
 * Renders a sentinel div that is used as the observation target.
 */
export function PricingSectionTracker() {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinelRef.current;

    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          trackChatSuitePricingView({});
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return <div ref={sentinelRef} aria-hidden="true" />;
}

/**
 * Fires chatsuite_apply_start on mount (user has navigated to the apply page).
 * Renders nothing visible.
 */
export function ApplyPageTracker({
  city,
  vertical,
}: {
  city: string;
  vertical: string;
}) {
  useEffect(() => {
    trackChatSuiteApplyStart({ city, vertical });
  }, [city, vertical]);

  return null;
}

/**
 * Calls trackChatSuiteApplyComplete with the provided props.
 * Used as a callback from LeadCaptureForm on successful submission.
 */
export function fireApplyComplete(props: {
  city: string;
  vertical: string;
  businessType: string;
  employeeCount: string;
}): void {
  trackChatSuiteApplyComplete(props);
}

/**
 * Wraps a pricing CTA link and fires chatsuite_pricing_view with the
 * selected tier name on click. Renders children as-is.
 */
export function PricingCtaTracker({
  tier,
  children,
}: {
  tier: string;
  children: React.ReactNode;
}) {
  return (
    <span
      onClick={() => trackChatSuitePricingView({ tier })}
      role="presentation"
    >
      {children}
    </span>
  );
}

/**
 * Wraps a feature card and fires chatsuite_feature_interest on click.
 * Renders children as-is.
 */
export function FeatureCardTracker({
  feature,
  children,
}: {
  feature: string;
  children: React.ReactNode;
}) {
  return (
    <div
      onClick={() => trackChatSuiteFeatureInterest({ feature })}
      role="presentation"
    >
      {children}
    </div>
  );
}

/**
 * Wraps the hero CTA and fires chatsuite_demo_click on click.
 * Renders children as-is.
 */
export function HeroCtaTracker({
  source,
  children,
}: {
  source: string;
  children: React.ReactNode;
}) {
  return (
    <span
      onClick={() => trackChatSuiteDemoClick({ source })}
      role="presentation"
    >
      {children}
    </span>
  );
}
